/**
 * Shared formatting helpers (dates, relative time, durations, ticket titles).
 * Also holds the UTC/epoch helpers (formerly utils/Date).
 */
import { TicketType } from "shared/Ticket";
import type { Ticket } from "shared/Ticket";


type Translate = (key: string) => string;
type Timestamp = number | string | Date | null | undefined;

/** Check if value looks like Unix epoch (seconds or milliseconds) */
export function isUnixEpoch(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  return /^\d{10,13}$/.test(str);
}

/** Convert Unix epoch (seconds or ms) to milliseconds */
function epochToMs(value: string | number): number {
  const num = Number(value);
  return num < 1e12 ? num * 1000 : num; // 10 digits = seconds
}

/** Format Unix epoch (seconds or ms) to readable UTC date string */
export function formatEpochToUTC(value: string | number, locale?: string): string {
  const ms = epochToMs(value);
  const date = new Date(ms);
  return date.toLocaleString(toIntlLocale(locale), {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }) + " UTC";
}

/** Format a value for display: if Unix epoch, format as UTC; otherwise pass through */
export function formatFieldValue(value: unknown, locale?: string): string {
  if (value === undefined || value === null) return "";
  if (isUnixEpoch(value)) return formatEpochToUTC(value as string | number, locale);
  return String(value);
}

const UTC_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: "UTC",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

/** Format any date-like value (epoch, ISO string, Date) to readable UTC string */
export function formatDateToUTC(value: unknown, locale?: string): string {
  if (value === undefined || value === null) return "";
  if (isUnixEpoch(value)) return formatEpochToUTC(value as string | number, locale);
  const date = new Date(value as string | number | Date);
  if (isNaN(date.getTime())) return String(value);
  return new Date(date).toLocaleString(toIntlLocale(locale), UTC_DATE_OPTIONS) + " UTC";
}

/** UTC calendar date as `YYYY-MM-DD` (epoch seconds/ms, ISO string or Date). Unlike `formatDate` this is not locale-dependent. */
export function formatIsoDate(toFormat: string | number | Date) {
  if (toFormat === undefined || toFormat === null) return "";
  let date: Date;
  if (typeof toFormat === "number") {
    const ms = toFormat < 1e12 ? toFormat * 1000 : toFormat; // Unix seconds vs ms
    date = new Date(ms);
  } else if (typeof toFormat === "string") {
    if (isUnixEpoch(toFormat)) {
      date = new Date(epochToMs(toFormat));
    } else {
      date = new Date(toFormat);
    }
  } else {
    date = toFormat;
  }
  if (isNaN(date.getTime())) return String(toFormat);
  return date.toISOString().split("T")[0];
}

/** i18n locale (es/en) to a BCP-47 tag; the app is Spanish-first, so a missing locale means es-ES (dd/mm/yyyy), not the browser's. */
export function toIntlLocale(locale?: string): string | undefined {
  if (!locale) return "es-ES";
  if (locale === "es") return "es-ES";
  if (locale === "en") return "en-US";
  return locale;
}

function toDate(ts: Timestamp): Date | null {
  if (ts === undefined || ts === null || ts === "" || ts === 0) return null;
  const date = ts instanceof Date ? ts : new Date(ts);
  return isNaN(date.getTime()) ? null : date;
}

/** Numeric date with zero-padded day/month, so es renders 05/03/2026 rather than 5/3/2026. */
const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };

/** Locale date only. Empty string when the value is missing/invalid. */
export function formatDate(ts: Timestamp, locale?: string): string {
  const date = toDate(ts);
  return date ? date.toLocaleDateString(toIntlLocale(locale), DATE_OPTIONS) : "";
}

/** Locale date + time. Empty string when the value is missing/invalid. */
export function formatDateTime(ts: Timestamp, locale?: string): string {
  const date = toDate(ts);
  return date ? date.toLocaleString(toIntlLocale(locale), { ...DATE_OPTIONS, hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
}

/**
 * Compact elapsed time: "5m", "2h", "3d". Anything under a minute is "1m".
 */
export function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (minutes < 1) return "1m";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  return `${days}d`;
}

/**
 * Day-relative label: time of day today, "Yesterday", "N days ago" (< 7 days),
 * otherwise the locale date. "-" when the value is missing.
 */
export function formatRelative(ts: Timestamp, t: Translate, locale?: string): string {
  const date = toDate(ts);
  if (!date) return "-";
  const intl = toIntlLocale(locale);
  const diffDays = Math.floor(Math.max(0, Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return date.toLocaleTimeString(intl, { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return t("common.yesterday") || "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} ${t("common.daysAgo") || "days ago"}`;
  }
  return date.toLocaleDateString(intl, DATE_OPTIONS);
}

interface TitleViewer {
  visualId: string;
}

/**
 * Ticket display title. Reports are shown from the viewer's perspective.
 * `isAdmin` shows both real names (used by staff views).
 */
export function getTicketTitle(
  ticket: Ticket,
  t: Translate,
  viewer?: TitleViewer | null,
  isAdmin = false,
): string {
  if (ticket.type === TicketType.SUPPORT) return ticket.title;
  if (ticket.type === TicketType.REPORT) {
    const accused = ticket.reportAccused;
    if (!viewer) return `${ticket.createdBy.visualId} > ${accused?.visualId ?? "-"}`;
    if (isAdmin) return `${ticket.createdBy.name} > ${accused?.name ?? "-"}`;
    if (accused) {
      if (viewer.visualId === ticket.createdBy.visualId) {
        return `${ticket.createdBy.name} > ${accused.visualId}`;
      } else if (viewer.visualId === accused.visualId) {
        return `${ticket.createdBy.visualId} > ${accused.name}`;
      }
      return `${ticket.createdBy.visualId} > ${accused.visualId} (${t("report.partner")})`;
    }
    return `${ticket.createdBy.visualId} > -`;
  }
  return ticket.title || "";
}
